import axios from 'axios';
import {
  NetworkRequest,
  NetworkRequestMultipart
} from '../../../../Utilities/NetworkRequests/NetworkRequests';
import { logError } from '../../../../../Utilities/logError';
import {
  type IClickBuyerOption,
  type ICostPerBindUploadResponse
} from './types';

const ENDPOINT = 'clickBuyerUploads';
export const BIND_DATA_CPC_BUYER_TYPE = 2;

export const getClickBuyersForUser = async (
  type?: number
): Promise<IClickBuyerOption[]> => {
  try {
    const { data } = await NetworkRequest(ENDPOINT, {
      action: 'getClickBuyerForUserId',
      ...(type === undefined ? {} : { type })
    });

    if (!data || data.success === false) {
      return [];
    }

    return Object.entries(data)
      .filter(([value]) => /^\d+$/.test(value))
      .map(([value, display]) => ({ value, display: String(display) }))
      .sort((a, b) => a.display.localeCompare(b.display));
  } catch (error) {
    logError(error);

    return [];
  }
};

export const toGroupingArray = (data: unknown): string[] => {
  if (Array.isArray(data)) {
    return data.map(String);
  }

  if (!data || typeof data !== 'object' || 'success' in data) {
    return [];
  }

  return Object.values(data as Record<string, unknown>).map(String);
};

export const getLeadDemoGrouping = async (
  buyerId: string,
  signal?: AbortSignal
): Promise<string[]> => {
  const { data } = await NetworkRequest(ENDPOINT, {
    signal,
    action: 'getLeadDemoGroupingForBuyer',
    buyer_id: buyerId
  });

  return toGroupingArray(data);
};

export const submitCostPerBindCsv = async (
  file: File,
  buyerId: string
): Promise<ICostPerBindUploadResponse> => {
  try {
    const { data } = await NetworkRequestMultipart(
      ENDPOINT,
      {
        csv: file,
        buyer_id: buyerId
      },
      ['csv'],
      'addBindTargets'
    );

    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return { success: false, message: 'Server error' };
    }

    return {
      success: false,
      message: `Failed to upload CSV: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    };
  }
};

export const getBindTargets = async (
  buyerId: string,
  signal?: AbortSignal
): Promise<Array<Record<string, unknown>>> => {
  const { data } = await NetworkRequest(ENDPOINT, {
    signal,
    action: 'getBindTargets',
    buyer_id: buyerId
  });

  if (!Array.isArray(data)) {
    throw new Error(data?.message ?? 'Bind targets could not be loaded.');
  }

  return data;
};
